import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  useTheme,
  Tooltip,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteOutlineIcon,
  MoreVert as MoreVertIcon,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowEditorSimple from "../../components/FlowBuilder/FlowEditorSimple";
import FlowEditorReactFlow from "../../components/FlowBuilder/FlowEditorReactFlow";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    padding: theme.spacing(1, 2),
  },
}));

const FlowBuilders = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  // eslint-disable-next-line no-unused-vars
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [flows, setFlows] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingFlow, setDeletingFlow] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [loadingFlow, setLoadingFlow] = useState(false);
  const isMountedRef = useRef(true);

  // ✅ Cleanup para prevenir actualizaciones en componente desmontado
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ✅ Si hay ID en la ruta, mostrar editor
  const currentPath = location.pathname;
  const isEditor = id !== undefined || currentPath === "/flowbuilders/new" || (currentPath.includes("/flowbuilders/") && currentPath.includes("/edit"));
  const isNewFlow = id === "new" || currentPath === "/flowbuilders/new";

  useEffect(() => {
    const path = location.pathname;
    const isEditorPath = path === "/flowbuilders/new" || (path.includes("/flowbuilders/") && path.includes("/edit"));
    const isNewPath = path === "/flowbuilders/new";
    
    if (isEditorPath && !isNewPath && id) {
      fetchFlowById();
    } else if (isNewPath) {
      setCurrentFlow({ name: "", flow: { nodes: [], edges: [] } });
    } else if (!isEditorPath) {
      fetchFlows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, id, searchParam, page, rowsPerPage]);

  const fetchFlowById = async () => {
    try {
      if (isMountedRef.current) {
        setLoadingFlow(true);
      }
      const { data } = await api.get(`/flowbuilders/${id}`);
      if (isMountedRef.current) {
        setCurrentFlow(data);
      }
    } catch (err) {
      if (isMountedRef.current) {
        toastError(err);
        history.push("/flowbuilders");
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingFlow(false);
      }
    }
  };

  const fetchFlows = async () => {
    try {
      if (isMountedRef.current) {
        setLoading(true);
      }
      const { data } = await api.get("/flowbuilders", {
        params: {
          searchParam: searchParam || "",
          pageNumber: page + 1,
          active: true,
        },
      });

      if (!isMountedRef.current) return;

      // ✅ Validar y mapear datos recibidos
      const flowsData = data.records || [];
      
      // ✅ Asegurar que todos los registros tengan ID válido y datos correctos
      const validFlows = flowsData.map((flow) => ({
        ...flow,
        id: flow?.id || flow?.Id || 0,
        name: flow?.name || flow?.Name || 'Sin nombre',
        active: flow?.active !== undefined ? flow.active : flow?.Active !== undefined ? flow.Active : false,
        createdAt: flow?.createdAt || flow?.CreatedAt || new Date()
      }));
      
      if (isMountedRef.current) {
        setFlows(validFlows);
      }
    } catch (err) {
      if (isMountedRef.current) {
        toastError(err);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value);
    setPage(0);
  };

  const handleNewFlow = () => {
    history.push("/flowbuilders/new");
  };

  const handleEditFlow = (flow) => {
    if (!flow || !flow.id) {
      toast.error("Error: No se pudo obtener el ID del flujo");
      return;
    }
    history.push(`/flowbuilders/${flow.id}/edit`);
  };

  const handleDeleteFlow = async () => {
    if (!deletingFlow || !deletingFlow.id) {
      toast.error("Error: No se pudo obtener el ID del flujo");
      return;
    }

    try {
      await api.delete(`/flowbuilders/${deletingFlow.id}`);
      toast.success(i18n.t("flowbuilders.toasts.deleted"));
      fetchFlows();
      setConfirmModalOpen(false);
      setDeletingFlow(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleMenuOpen = (event, flow) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedFlow(flow);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedFlow(null);
  };

  const handleMenuEdit = () => {
    if (selectedFlow) {
      handleEditFlow(selectedFlow);
    }
    handleMenuClose();
  };

  const handleMenuDelete = () => {
    if (selectedFlow) {
      setDeletingFlow(selectedFlow);
      setConfirmModalOpen(true);
    }
    handleMenuClose();
  };

  const handleSaveFlow = async (flowData, isAutoSave = false) => {
    try {
      if (isNewFlow) {
        // Crear nuevo flujo
        const { data } = await api.post("/flowbuilders", {
          name: flowData.name || "Nuevo Flujo",
          flow: flowData.flow,
          active: true,
        });
        // ✅ NO redirigir - el usuario permanece en el editor
        // Solo actualizar el ID si es necesario
        if (data?.id) {
          // Actualizar la URL sin recargar
          window.history.replaceState({}, "", `/flowbuilders/${data.id}/edit`);
        }
      } else {
        // Actualizar flujo existente
        await api.put(`/flowbuilders/${id}`, {
          name: flowData.name,
          flow: flowData.flow,
        });
        // ✅ NO redirigir - el usuario permanece en el editor
      }
    } catch (err) {
      toastError(err);
      throw err;
    }
  };

  const handleCancelEditor = () => {
    history.push("/flowbuilders");
  };

  // ✅ Si es editor, mostrar el editor visual
  const path = location.pathname;
  const shouldShowEditor = path === "/flowbuilders/new" || (path.includes("/flowbuilders/") && path.includes("/edit"));
  const isNewPath = path === "/flowbuilders/new";
  
  if (shouldShowEditor) {
    if (loadingFlow && !isNewPath && id) {
      return (
        <MainContainer>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography>{i18n.t("flowbuilders.loading")}</Typography>
          </Box>
        </MainContainer>
      );
    }

    return (
      <MainContainer>
        <FlowEditorReactFlow
          flowId={isNewPath ? null : id}
          initialFlow={currentFlow || (isNewPath ? { name: "", flow: { nodes: [], edges: [] } } : null)}
          onSave={handleSaveFlow}
          onCancel={handleCancelEditor}
        />
      </MainContainer>
    );
  }

  // ✅ Si no es editor, mostrar lista
  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("flowbuilders.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewFlow}
            startIcon={<AddIcon />}
          >
            {i18n.t("flowbuilders.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <TextField
            placeholder={i18n.t("flowbuilders.searchPlaceholder")}
            type="search"
            size="small"
            variant="outlined"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.name")}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.status")}
              </TableCell>
              <TableCell className={classes.customTableCell}>
                {i18n.t("flowbuilders.table.createdAt")}
              </TableCell>
              <TableCell className={classes.customTableCell} align="center">
                {i18n.t("flowbuilders.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableRowSkeleton columns={4} />}
            {!loading && flows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("flowbuilders.noFlows")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              flows.map((flow, index) => (
                <TableRow key={flow?.id || `flow-${index}`} hover>
                  <TableCell 
                    className={classes.customTableCell}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleEditFlow(flow)}
                  >
                    <Typography 
                      variant="body2" 
                      style={{ 
                        fontWeight: 500,
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        textDecorationColor: 'transparent',
                        transition: 'text-decoration-color 0.2s',
                        '&:hover': {
                          textDecorationColor: theme.palette.primary.main,
                        }
                      }}
                    >
                      {flow?.name || 'Sin nombre'}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    <Chip
                      label={flow?.active ? i18n.t("flowbuilders.status.active") : i18n.t("flowbuilders.status.inactive")}
                      color={flow?.active ? "primary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell className={classes.customTableCell}>
                    {flow?.createdAt ? new Date(flow.createdAt).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell className={classes.customTableCell} align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditFlow(flow)}
                        style={{ color: theme.palette.primary.main }}
                        title={i18n.t("flowbuilders.actions.edit") || "Editar"}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeletingFlow(flow);
                          setConfirmModalOpen(true);
                        }}
                        style={{ color: theme.palette.error.main }}
                        title={i18n.t("flowbuilders.actions.delete") || "Eliminar"}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={flows.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
          labelRowsPerPage={i18n.t("flowbuilders.rowsPerPage")}
        />
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuEdit}>
            <EditIcon style={{ marginRight: 8, fontSize: 18 }} />
            {i18n.t("flowbuilders.actions.edit")}
          </MenuItem>
          <MenuItem onClick={handleMenuDelete}>
            <DeleteOutlineIcon style={{ marginRight: 8, fontSize: 18 }} />
            {i18n.t("flowbuilders.actions.delete")}
          </MenuItem>
        </Menu>
        <ConfirmationModal
          title={i18n.t("flowbuilders.confirmationModal.deleteTitle")}
          open={confirmModalOpen}
          onClose={() => {
            setConfirmModalOpen(false);
            setDeletingFlow(null);
          }}
          onConfirm={handleDeleteFlow}
        >
          {i18n.t("flowbuilders.confirmationModal.deleteMessage")}
        </ConfirmationModal>
      </Paper>
    </MainContainer>
  );
};

export default FlowBuilders;
